<template>
  <div
    class="modal fade p-2"
    :class="{ show: isOpen }"
    tabindex="-1"
    :style="{ display: isOpen ? 'block' : 'none' }"
    aria-modal="true"
    role="dialog"
    data-test-id="release-notes-dialog"
    @wheel.stop
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ viewingAll ? $t('releaseNotesBrowseTitle') : $t('releaseNotesTitle') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            :aria-label="$t('close')"
            data-test-id="release-notes-close"
            @click="close"
          ></button>
        </div>
        <div class="release-notes-hint" data-test-id="release-notes-ifu-hint">
          <font-awesome-icon icon="fa-solid fa-circle-question" />
          <span>{{ $t('releaseNotesIfuHint') }}</span>
        </div>
        <div class="modal-body">
          <div v-if="isLoading" class="text-center py-4" data-test-id="release-notes-loading">
            <font-awesome-icon icon="fa-solid fa-spinner" spin size="lg" />
          </div>
          <template v-else>
            <div
              v-for="note in visibleReleaseNotes"
              :key="note.version"
              class="release-note"
              data-test-id="release-note-entry"
            >
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="version-chip">v{{ note.version }}</span>
                <span class="text-muted small">
                  {{ $t('releaseNotesReleasedOn', { date: formatDateValue(note.releasedAt) }) }}
                </span>
              </div>
              <div class="release-note-body" v-html="localizedBody(note)"></div>
            </div>
            <div v-if="canSeeMore" class="text-center">
              <button
                type="button"
                class="see-more-btn"
                data-test-id="release-notes-see-more"
                @click="revealMore"
              >
                {{ $t('releaseNotesSeeMore') }}
                <font-awesome-icon icon="fa-solid fa-chevron-down" class="ms-1" />
              </button>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            v-if="viewingAll && truncated"
            type="button"
            class="btn btn-secondary"
            data-test-id="release-notes-load-older"
            @click="loadOlderVersions"
          >
            {{ $t('releaseNotesLoadOlder') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            data-test-id="release-notes-got-it"
            @click="close"
          >
            <font-awesome-icon icon="fa-solid fa-check" class="me-1" />
            {{ $t('releaseNotesGotIt') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({ name: 'ReleaseNotesDialog' });

import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';
import { Languages } from '@/common/types';
import { formatDateValue } from '@/common/utils';
import { ReleaseNote } from '@/modules/release-notes/types';

const ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'b',
  'em',
  'i',
  'br',
  'a'
];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

const props = withDefaults(defineProps<{ viewAll?: boolean }>(), {
  viewAll: false
});

const store = useStore();
const { locale } = useI18n();

const isOpen = ref(false);
const revealCount = ref(1);
const viewingAll = ref(props.viewAll);

const releaseNotes = computed<ReleaseNote[]>(
  () => store.state.releaseNotes.releaseNotes.data ?? []
);
const truncated = computed<boolean>(() => store.state.releaseNotes.truncated);
const isLoading = computed<boolean>(() => store.state.releaseNotes.releaseNotes.isLoading);

const visibleReleaseNotes = computed<ReleaseNote[]>(() =>
  viewingAll.value ? releaseNotes.value : releaseNotes.value.slice(0, revealCount.value)
);
const hasMoreToReveal = computed<boolean>(
  () => !viewingAll.value && releaseNotes.value.length > revealCount.value
);
const canSeeMore = computed<boolean>(() => !viewingAll.value && releaseNotes.value.length > 0);

const revealMore = async (): Promise<void> => {
  if (hasMoreToReveal.value) {
    revealCount.value = releaseNotes.value.length;
    return;
  }

  viewingAll.value = true;
  await store.dispatch('releaseNotes/getReleaseNotes', { viewAll: true });
};

const localizedBody = (note: ReleaseNote): string => {
  const body = note.body[locale.value as Languages] ?? Object.values(note.body)[0] ?? '';

  return DOMPurify.sanitize(body, { ALLOWED_TAGS, ALLOWED_ATTR });
};

const open = async (): Promise<void> => {
  if (props.viewAll) isOpen.value = true;

  await store.dispatch('releaseNotes/getReleaseNotes', { viewAll: props.viewAll });
  revealCount.value = 1;
  viewingAll.value = props.viewAll;
  isOpen.value = props.viewAll || releaseNotes.value.length > 0;
};

const close = (): void => {
  const shouldAcknowledge = !props.viewAll && releaseNotes.value.length > 0;

  isOpen.value = false;

  if (shouldAcknowledge) {
    store.dispatch('releaseNotes/acknowledgeReleaseNotes');
  }
};

const loadOlderVersions = (): void => {
  const before = releaseNotes.value[releaseNotes.value.length - 1]?.version;

  store.dispatch('releaseNotes/getReleaseNotes', { viewAll: true, before });
};

onMounted(() => {
  if (!props.viewAll) open();
});

defineExpose({ open });
</script>

<style lang="scss" src="./release-notes-dialog.component.scss" scoped />
